/**
 * ENLACES Y HERRAMIENTAS DE LA FICHA
 * ---------------------------------------------------------------------------------------------------------------------
 * Comportamiento del bloque "product_tools" (snippet 'product-tools'): la fila de enlaces con icono que va bajo el
 * selector de tallas ("Probador virtual", "Tabla de medidas"...).
 *
 * Hace dos cosas, las dos alrededor de la misma regla: un enlace solo debe existir si lleva a algún sitio.
 *
 *   1. Poda. Liquid ya descarta los destinos que no existen en el schema, pero no puede saber si el bloque destino ha
 *      llegado a pintar algo: un desplegable que depende de las colecciones del producto, por ejemplo, queda vacío en
 *      parte del catálogo. Eso solo se ve en el navegador, así que aquí se quitan los enlaces cuyo destino no esté o
 *      esté vacío. Si no queda ninguno, se oculta la fila entera.
 *
 *   2. Desplazamiento. El salto nativo del navegador deja el destino pegado al borde superior (y debajo de la cabecera
 *      fija) y, si el destino es un desplegable cerrado, lo deja cerrado. Aquí se abre primero y se desplaza después,
 *      con la animación suave del sistema y respetando "prefers-reduced-motion".
 *
 * Es un elemento personalizado y no un listener suelto porque 'product-rerender' reemplaza el HTML de los bloques al
 * cambiar de variante: con connectedCallback la poda se vuelve a aplicar sola sobre el HTML nuevo.
 */

/* El destino existe pero no ha pintado nada: es el mismo caso que ".product-info__block-item:empty" en el CSS. */
const isEmpty = (element) => element.children.length === 0 && element.textContent.trim() === '';

const resolveTarget = (link) => {
  const hash = link.hash;

  if (!hash || hash === '#') return null;

  try {
    return document.querySelector(hash);
  } catch (error) {
    /* Un id con caracteres raros no es un selector válido */
    return null;
  }
};

class ProductTools extends HTMLElement {
  connectedCallback() {
    /* El destino puede estar más abajo en el documento y todavía no haberse parseado */
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#prune(), { once: true });
    } else {
      this.#prune();
    }

    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #prune() {
    if (!this.isConnected) return;

    for (const link of this.querySelectorAll('[data-product-tools-anchor]')) {
      const target = resolveTarget(link);

      if (!target || isEmpty(target)) link.remove();
    }

    this.hidden = this.childElementCount === 0;
  }

  #onClick = (event) => {
    const link = event.target.closest('[data-product-tools-anchor]');

    if (!link || !this.contains(link)) return;

    const target = resolveTarget(link);

    if (!target) return;

    event.preventDefault();

    /* Un desplegable cerrado no se puede leer: se abre antes de desplazar, y sin animación para que la altura ya sea
       la definitiva cuando el navegador calcule a dónde tiene que llegar el scroll. */
    const accordion = target.matches('accordion-disclosure') ? target : target.querySelector('accordion-disclosure');

    if (accordion) {
      if (typeof accordion.open === 'function') {
        accordion.open({ instant: true });
      } else {
        const details = accordion.querySelector('details');

        if (details) {
          details.open = true;
          details.setAttribute('aria-expanded', 'true');
        }
      }
    }

    /* El hueco bajo la cabecera fija no se calcula aquí: lo pone el CSS con "scroll-padding-top" en <html> (el mismo
       que usa el salto nativo del navegador) más el "scroll-margin" del destino. Leer --sticky-area-height desde JS no
       serviría: es un calc() y las variables sin registrar no se evalúan al consultarlas. */
    target.scrollIntoView({
      block: 'start',
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  };
}

if (!customElements.get('product-tools')) {
  customElements.define('product-tools', ProductTools);
}
